import DotsVertical from '@mdi/svg/svg/dots-vertical.svg?react';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo } from 'react';
import styled from 'styled-components';
import { EMPTY_ARRAY } from '../../constants';
import { COLOR, FONT_SIZE, SIZE, subtitleBarHeight, titleBarHeight, titleGap } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { Button } from '../Button';
import { ButtonMenu } from '../ButtonMenu';
import { MDIIcon } from '../MDIIcon';
import { DirectionIndicator } from '../SVG';
import { type CardAction, type CardHeaderProps } from './Card.types';

const CardHeader: React.FC<CardHeaderProps> = memo(
  ({
    actions = EMPTY_ARRAY as CardAction[],
    actionsElement,
    buttonMenuButtonTooltip,
    className,
    handleToggle,
    icon,
    isCollapsible = false,
    isOpen = true,
    menuItemHandlerData,
    menuItems,
    menuItemVisibilityHandler,
    menuElement,
    meta,
    subTitle,
    title,
  }) => {
    const headerClass = classNames('card-header', className, {
      clickable: isCollapsible,
      subtitle: subTitle,
    });
    const headerOnClick = isCollapsible && handleToggle ? handleToggle : () => null;

    return (
      <HeaderWrapper
        className={headerClass}
        onClick={headerOnClick}
        onKeyDown={a11yKey(headerOnClick)}
        data-testid="card-header"
      >
        <TitleSection className="title">
          {icon && <IconSpan className="icon">{icon}</IconSpan>}
          {title && (
            <Title data-testid={`${title}-card-title`}>
              {title}
              {!isEmpty(meta) && <HeaderMeta className="header-meta">{meta}</HeaderMeta>}
            </Title>
          )}
        </TitleSection>
        <ActionBarSection className="action-bar">
          {actionsElement}
          {actions &&
            actions.map(({ icon, text, onClick, tooltip }, index) => {
              return (
                <Button
                  className="action-button"
                  dataTestId={`${title}-card-action`}
                  icon={icon}
                  iconOnly
                  key={index}
                  onClick={onClick}
                  title={text}
                  tooltip={tooltip}
                  type="clear"
                />
              );
            })}
          {menuElement}
          {!menuElement && menuItems && (
            <ButtonMenu
              buttonIcon={<MDIIcon icon={DotsVertical} />}
              buttonIconOnly={true}
              buttonType="clear"
              className="action-button menu-button"
              dataTestId={`${title}-card-menu`}
              menuItemHandlerData={menuItemHandlerData}
              menuItems={menuItems}
              menuItemVisibilityHandler={menuItemVisibilityHandler}
              buttonTooltip={buttonMenuButtonTooltip}
            />
          )}
          {isCollapsible && (
            <Button
              className="action-button"
              icon={<DirectionIndicator direction={isOpen ? 'up' : 'down'} />}
              onClick={handleToggle}
              type="clear"
              dataTestId={`${title}-card-show`}
            />
          )}
        </ActionBarSection>
        {subTitle && (
          <SubtitleSection className="sub-title">
            <Subtitle>{subTitle}</Subtitle>
          </SubtitleSection>
        )}
      </HeaderWrapper>
    );
  },
);

// Styled Components
const HeaderWrapper = styled.header`
  align-items: center;
  border-radius: ${SIZE['4']} ${SIZE['4']} 0 0;
  box-sizing: border-box;
  display: grid;
  grid-template: ${titleBarHeight} / auto 1fr;
  padding: ${SIZE['16']};
  width: 100%;
  z-index: 1000;

  &.subtitle {
    grid-template: ${titleBarHeight} ${subtitleBarHeight} / auto 1fr;
    grid-gap: ${titleGap} 0;
  }

  &.clickable {
    cursor: pointer;
  }
`;

const TitleSection = styled.section`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  grid-column: 1 / span 1;
`;

const IconSpan = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${SIZE['8']};

  path,
  polygon {
    fill: ${COLOR['light-gray']};
  }
`;

const Title = styled.h2`
  color: ${COLOR['dark-gray-dark']};
  font-size: ${FONT_SIZE['18']};
  font-weight: 500;
  line-height: calc(${titleBarHeight} + 3px);
  margin: 0;
  max-width: 315px;
  overflow: hidden;
  padding: 0;
  pointer-events: auto;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderMeta = styled.span`
  font-weight: 400;
  margin-left: ${SIZE['8']};
`;

const SubtitleSection = styled.section`
  grid-column: 1 / span 2;
`;

const Subtitle = styled.h3`
  color: ${COLOR['dark-gray-lighter']};
  display: block;
  font-size: ${FONT_SIZE['14']};
  font-weight: 300;
  line-height: 1.2;
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  padding: 0;
  pointer-events: auto;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionBarSection = styled.section`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  grid-column: 2 / span 1;
  height: ${titleBarHeight};
  margin-left: auto;

  .icon {
    display: inline-block;
    min-width: 15px;
    text-align: center;
    top: 0;
  }

  a {
    cursor: pointer;
    outline: none;
    pointer-events: all;
  }

  > a,
  .action-button > a,
  a.action-button,
  .action-button button.styled.clear {
    display: inline-block;
    position: relative;
    color: ${COLOR['primary']};

    &:focus {
      color: ${COLOR['turquoise-darker']};
      outline: none;
    }
  }

  > .action {
    margin-left: 7px;
    position: relative;
  }
`;

// Display Name
CardHeader.displayName = 'CardHeader';

export default CardHeader;
